// Event chat (T-601, DATA_MODEL → Chat). Paged by the per-event `seq` (D26), never by id.
import { z } from 'zod';

export const MESSAGE_BODY_MAX = 1000;
export const MESSAGES_PAGE_DEFAULT = 50;
export const MESSAGES_PAGE_MAX = 100;
/** PRIVACY §4: chat messages per user per minute. */
export const CHAT_RATE_LIMIT_PER_MINUTE = 20;
/** Writes close this long after the event ends (and at once when it is cancelled). */
export const CHAT_OPEN_AFTER_END_MS = 24 * 60 * 60 * 1000;

/** Quick statuses (S11, T-904): «Я на місці» / «Запізнююсь». */
export const CHAT_STATUSES = ['arrived', 'late'] as const;
export type ChatStatus = (typeof CHAT_STATUSES)[number];
/** Statuses make sense around the meetup only: from 2 h before the start until the end. */
export const CHAT_STATUS_FROM_BEFORE_START_MS = 2 * 60 * 60 * 1000;

const seq = z.coerce.number().int().min(0);

/**
 * `after_seq`: newer messages, oldest first (polling). `before_seq`: older history.
 * Neither: the latest page. Not both.
 */
export const messagesQuerySchema = z
	.object({
		after_seq: seq.optional(),
		before_seq: seq.optional(),
		limit: z.coerce.number().int().min(1).max(MESSAGES_PAGE_MAX).default(MESSAGES_PAGE_DEFAULT),
	})
	.refine((query) => query.after_seq === undefined || query.before_seq === undefined, {
		message: 'use after_seq or before_seq, not both',
	});

const personSchema = z.object({
	id: z.uuid(),
	/** Null: the account was deleted (T-804); clients show a neutral placeholder. */
	displayName: z.string().nullable(),
	photoUrl: z.url().nullable(),
});

export const chatMessageSchema = z.object({
	id: z.uuid(),
	seq: z.number().int(),
	kind: z.enum(['text', 'status_arrived', 'status_late', 'system']),
	/** Null for system and status messages: clients render those in their own language. */
	body: z.string().nullable(),
	system: z.object({ type: z.literal('joined'), user: personSchema }).nullable(),
	sender: personSchema.nullable(),
	mine: z.boolean(),
	createdAt: z.iso.datetime(),
});

export const messagesResponseSchema = z.object({
	/** Ascending by `seq`. Hidden/deleted messages are left out, so seqs can have gaps. */
	items: z.array(chatMessageSchema),
	/**
	 * Poll with `after_seq` = this value. Null for `before_seq` pages. Covers left-out
	 * messages too, so polling never stalls on them.
	 */
	nextAfterSeq: z.number().int().nullable(),
	/** `after_seq`: more newer messages wait (fetch again now). Otherwise: older ones exist. */
	hasMore: z.boolean(),
	/** False once the chat is closed (cancelled or end + 24 h): read-only (D37). */
	canWrite: z.boolean(),
});

/** Made by the client per message; resending the same id returns the same message. */
const clientId = z.uuid();

/** A text message, or a quick status (`status_arrived` / `status_late` messages). */
export const sendMessageRequestSchema = z.union([
	z.strictObject({ body: z.string().trim().min(1).max(MESSAGE_BODY_MAX), clientId }),
	z.strictObject({ status: z.enum(CHAT_STATUSES), clientId }),
]);

export const sendMessageResponseSchema = z.object({ message: chatMessageSchema });

/** `POST /v1/events/:id/read`: the viewer has seen everything up to `seq`. */
export const markReadRequestSchema = z.strictObject({ seq: z.number().int().min(0) });
export const markReadResponseSchema = z.object({ lastReadSeq: z.number().int() });

/** One row of S10 (Chats list). */
export const chatSummarySchema = z.object({
	eventId: z.uuid(),
	title: z.string(),
	category: z.string(),
	coverUrl: z.url().nullable(),
	startAt: z.iso.datetime(),
	/** The newest visible message, or null if the chat is empty. */
	lastMessage: chatMessageSchema.nullable(),
	/** Messages from others after the viewer's read marker. */
	unread: z.number().int(),
	/** Read-only (cancelled or end + 24 h, D37). */
	closed: z.boolean(),
	/** T-1335: an accepted participant's open chat they have never opened (no `chat_reads` row). */
	isNew: z.boolean(),
});

/** `GET /v1/me/chats`: newest activity first; at most `CHATS_LIST_LIMIT` rows. */
export const myChatsResponseSchema = z.object({
	items: z.array(chatSummarySchema),
	totalUnread: z.number().int(),
});
export const CHATS_LIST_LIMIT = 100;

export type ChatMessage = z.infer<typeof chatMessageSchema>;
export type ChatSummary = z.infer<typeof chatSummarySchema>;
export type MyChatsResponse = z.infer<typeof myChatsResponseSchema>;
export type MessagesQuery = z.infer<typeof messagesQuerySchema>;
export type MessagesResponse = z.infer<typeof messagesResponseSchema>;
export type SendMessageRequest = z.input<typeof sendMessageRequestSchema>;
